import { create } from 'zustand';
import api from '../services/api';
import { toast } from 'react-toastify';

const MAX_IMAGE_SIDE = 1600;

// Shrinks big photos/screenshots before upload so sending stays quick. Falls back to the original file.
async function compressImage(file) {
  if (file.size < 300 * 1024 || file.type === 'image/gif' || typeof createImageBitmap !== 'function') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

// Mirrors the backend: title is the first line of the message
const titleFrom = (text, hasImages) => {
  const firstLine = (text || '').trim().split('\n')[0].trim();
  if (!firstLine) return hasImages ? 'Image ticket' : ''; 
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}...` : firstLine;
};

export const useTicketStore = create((set, get) => ({
  tickets: [],
  currentTicket: null,
  loading: false,

  fetchTickets: async (params = {}) => {
    set({ loading: true });
    try {
      const query = new URLSearchParams(params).toString();
      const res = await api.get(`/tickets${query ? '?' + query : ''}`);
      // Keep tickets that are still being sent
      set(state => ({ tickets: [...state.tickets.filter(t => t.pending), ...res.data.tickets], loading: false }));
    } catch (error) {
      set({ loading: false });
      toast.error('Failed to fetch tickets');
    }
  },

  fetchTicket: async (id) => {
    set({ loading: true });
    try {
      const res = await api.get(`/tickets/${id}`);
      set({ currentTicket: res.data.ticket, loading: false });
      return res.data.ticket;
    } catch (error) {
      set({ loading: false });
      toast.error('Failed to fetch ticket');
      return null;
    }
  },

  // Optimistic create: the ticket shows up in the list right away ("Sending…"),
  // images are compressed + uploaded and the ticket saved in the background.
  sendTicket: async ({ project, description, priority, files = [], user }) => {
    const tempId = `tmp-${Date.now()}`;
    const localUrls = files.map(f => URL.createObjectURL(f));
    const now = new Date().toISOString();
    const temp = {
      _id: tempId,
      ticketNumber: '',
      title: titleFrom(description, files.length > 0),
      description,
      priority,
      status: 'open',
      project: { _id: project._id, name: project.name },
      createdBy: user,
      createdAt: now,
      updatedAt: now,
      attachments: localUrls.map((url, i) => ({ url, fileType: files[i].type })),
      pending: true
    };
    set(state => ({ tickets: [temp, ...state.tickets] }));

    try {
      let attachments = [];
      if (files.length) {
        const formData = new FormData();
        (await Promise.all(files.map(compressImage))).forEach(f => formData.append('images', f));
        const up = await api.post('/tickets/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        attachments = up.data.attachments;
      }
      const res = await api.post('/tickets', { project: project._id, description, priority, attachments });
      set(state => ({ tickets: state.tickets.map(t => (t._id === tempId ? res.data.ticket : t)) }));
      return res.data.ticket;
    } catch (error) {
      set(state => ({ tickets: state.tickets.filter(t => t._id !== tempId) }));
      toast.error(error.response?.data?.message || 'Ticket could not be sent');
      return null;
    } finally {
      // Give the real (uploaded) images a moment to replace the local previews
      setTimeout(() => localUrls.forEach(URL.revokeObjectURL), 5000);
    }
  },

  updateStatus: async (id, status) => {
    try {
      const res = await api.put(`/tickets/${id}/status`, { status });
      set(state => ({
        tickets: state.tickets.map(t => t._id === id ? res.data.ticket : t),
        currentTicket: state.currentTicket?._id === id ? res.data.ticket : state.currentTicket
      }));
      toast.success('Status updated');
      return res.data.ticket;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
      return null;
    }
  },

  // userIds replaces the full assignee list ([] = unassign)
  assignTicket: async (id, userIds) => {
    try {
      const res = await api.put(`/tickets/${id}/assign`, { userIds });
      set(state => ({
        tickets: state.tickets.map(t => t._id === id ? res.data.ticket : t),
        currentTicket: state.currentTicket?._id === id ? res.data.ticket : state.currentTicket
      }));
      toast.success('Assignees updated');
      return res.data.ticket;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign ticket');
      return null;
    }
  },

  addComment: async (id, content) => {
    try {
      const res = await api.post(`/tickets/${id}/comments`, { content });
      set(state => ({
        currentTicket: state.currentTicket?._id === id ? res.data.ticket : state.currentTicket
      }));
      return res.data.ticket;
    } catch (error) {
      toast.error('Failed to add comment');
      return null;
    }
  }
}));
